<template>
  <div class="board-performance-report">
    <div class="d-flex justify-content-between align-items-baseline mb-2">
      <back-with-title
        :title="$t(`Team Performance`)"
        :description="!!dateRange ? `(${dateRange})` : ''"
        route-name="project-board"
        :params="{ boardUid: $route.params.boardUid }"
      />

      <b-tabs
        v-model="activeTab"
        pills
        content-class="d-flex justify-content-end"
        class="w-75"
      >
        <b-tab
          v-for="(item, index) in ranges"
          :id="item.value"
          :key="index"
          lazy
          @click="onTabChange(item.value)"
        >
          <template #title>
            <span
              v-b-tooltip.top="IS_MOBILE() ? '' : item.description"
              class="font-weight-bold"
            >
              {{ item.title }}
            </span>

            <div v-if="item.value === 'CUSTOM'">
              <date-range-picker
                trigger-target="CUSTOM___BV_tab_button__"
                :start-date="timeRange.customStart"
                :end-date="timeRange.customEnd"
                @change="({ startDate, endDate }) => {
                  timeRange.customStart = startDate
                  timeRange.customEnd = endDate
                  timeRange.preset = 'CUSTOM'
                }"
              />
            </div>
          </template>
        </b-tab>
      </b-tabs>
    </div>

    <b-tabs
      :key="activePerformanceTab"
      v-model="activePerformanceTab"
      class="no-padding-tabs"
      nav-class="nav-left"
      content-class="col-12 col-md-9 mt-1 mt-md-0"
      nav-wrapper-class="col-md-3 col-12"
      pills
      vertical
    >
      <b-tab lazy>
        <template #title>
          <span class="font-weight-bold p-1">
            <feather-icon icon="SettingsIcon" />
            {{ $t("Team Performance Report") }}
          </span>
        </template>

        <team-performance :time-range="timeRange" />
      </b-tab>

      <b-tab :disabled="true">
        <template #title>
          <hr style="width: 20%">
          <span class="font-weight-bold">{{ $t('Member Performance') }}</span>
          <hr style="width: 20%">
        </template>
      </b-tab>

      <!-- Member Performance -->
      <b-tab
        v-for="member in members"
        :key="member.uuid"
        lazy
      >
        <template #title>
          <b-avatar
            :src="getStorage(member.avatar)"
            :text="getInitials(member.name)"
          />
          <span class="font-weight-bold ml-1">
            {{ member.name }}
          </span>
        </template>

        <member-performance
          :member="member"
          :time-range="timeRange"
        />
      </b-tab>
    </b-tabs>
  </div>
</template>

<script>
import {
  BTabs,
  BTab,
  BAvatar,
} from 'bootstrap-vue'
import BackWithTitle from '@/views/common/components/BackWithTitle.vue'
import { mapActions, mapGetters } from 'vuex'
import DateRangePicker from '@/views/common/components/DateRangePicker.vue'
import moment from 'moment'
import MemberPerformance from './components/MemberPerformance.vue'
import TeamPerformance from './components/TeamPerformance.vue'

export default {
  components: {
    BTabs,
    BTab,
    BAvatar,
    MemberPerformance,
    TeamPerformance,
    DateRangePicker,
    BackWithTitle,
  },
  data() {
    return {
      activeTab: 0,
      activePerformanceTab: 0,
      ranges: [
        {
          title: this.$t('Last 7 Days'),
          value: 'LAST_7_DAYS',
        },
        {
          title: this.$t('Last 30 Days'),
          value: 'LAST_30_DAYS',
        },
        {
          title: this.$t('Last 3 Months'),
          value: 'LAST_90_DAYS',
        },
        {
          title: this.$t('Custom Range'),
          value: 'CUSTOM',
        },
      ],
      timeRange: {
        preset: 'LAST_7_DAYS',
        customStart: null,
        customEnd: null,
      },
    }
  },
  computed: {
    ...mapGetters('boards', {
      board: 'GET_CURRENT_BOARD',
    }),
    boardUid() {
      return this.base58Converter(this.$route.params.boardUid, false)
    },
    members() {
      return this.board?.members?.data || []
    },
    memberFromQuery() {
      return this.base58Converter(this.$route.query.memberUid, false)
    },
    dateRange() {
      if (this.timeRange.preset === 'CUSTOM' && this.timeRange.customStart && this.timeRange.customEnd) {
        return this.FORMAT_DATE_RANGE(this.timeRange.customStart, this.timeRange.customEnd)
      }
      const today = moment()
      let startDate = moment().subtract(6, 'days')

      switch (this.timeRange.preset) {
      case 'LAST_7_DAYS':
        return this.FORMAT_DATE_RANGE(startDate, today)
      case 'LAST_30_DAYS':
        startDate = moment().subtract(29, 'days')
        return this.FORMAT_DATE_RANGE(startDate, today)
      case 'LAST_90_DAYS':
        startDate = moment().subtract(89, 'days')
        return this.FORMAT_DATE_RANGE(startDate, today)
      default:
        return ''
      }
    },
  },
  watch: {
    board: {
      handler(val) {
        if (val && this.memberFromQuery) {
          this.$nextTick(() => {
            this.activePerformanceTab = this.members.findIndex(m => m.uuid === this.memberFromQuery) + 2 || 0
          })
        } else {
          this.$nextTick(() => {
            this.activePerformanceTab = 0
          })
        }
      },
      immediate: true,
    },
  },
  mounted() {
    if (!this.board) {
      this.fetchBoard({ uuid: this.boardUid })
    }
  },
  methods: {
    ...mapActions('boards', ['fetchBoard']),
    onTabChange(value) {
      this.timeRange.preset = value
      if (value !== 'CUSTOM') {
        this.timeRange.customStart = null
        this.timeRange.customEnd = null
      }
    },
  },
}
</script>

<style lang="scss">
.no-padding-tabs {
  .nav-pills .nav-link {
    padding: 0.25rem !important;
  }
}
</style>
